import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Stack,
  Grid,
  Box,
  Fade,
  FormHelperText,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import {
  getOnboardingUserIdFromLocalStorage,
  getUrlConvertedString,
} from '../../../helper';
import {
  getOnboardingState,
  saveOnboarding,
} from '../../../store/V2/OnboardingSlice';
import { actionButtonSelectStyles, actionButtonStyles } from './Step2';
import { backButtonProps } from './Step1';
import CustomStepper from './components/CustomStepper';
import DarkLoadingButton from '../../../components/V2/Buttons';

type Step6Props = {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
};

const Step6: React.FC<Step6Props> = ({ onNext, onBack, currentStep }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const { onboardingData, status: onboardingStatus } = useAppSelector(
    getOnboardingState
  );
  const onboardingUserId = getOnboardingUserIdFromLocalStorage();

  // State
  const [isError, setIsError] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    onboardingData?.goal || null
  );

  const isLoading = onboardingStatus === 'loading';

  useEffect(() => {
    if (onboardingData?.goal) {
      setSelectedOption(onboardingData.goal);
    }
  }, [onboardingData?.goal]);

  const goalOptions = [
    'I want to catch up / improve my basics',
    'I want help with school homework or topics',
    'I want to prepare for an exam',
    'I want to revise and get top scores',
  ];

  const handleContinue = async () => {
    if (selectedOption) {
      const res: any = await dispatch(
        saveOnboarding({
          _id: onboardingUserId,
          goal: selectedOption,
        })
      );

      if (res.payload.curriculum && res.payload.subject && res.payload.grade) {
        const urlCurriculum = getUrlConvertedString(res.payload.curriculum);
        const urlSubject = getUrlConvertedString(res.payload.subject);
        const urlGrade = getUrlConvertedString(res.payload.grade);

        const url = `${process.env.PUBLIC_URL}/portal/tutors?curriculum=${urlCurriculum}&subject=${urlSubject}&grade=${urlGrade}&fromOnboarding=true`;

        navigate(url);
      }
    } else {
      setIsError(true);
    }
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left section */}
      <Grid
        item
        xs={12}
        sm={4}
        md={6}
        sx={{
          backgroundColor: '#FFF0E1',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button {...backButtonProps} onClick={onBack}>
          Back
        </Button>
        <img
          src={`${process.env.PUBLIC_URL}/images/onboarding/img-step6.png`}
          alt="step6"
        />
      </Grid>

      {/* Right section */}
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Mobile Back button */}
        <Box display={{ xs: 'block', sm: 'none' }}>
          <Button
            {...backButtonProps}
            sx={{ ...backButtonProps.sx, left: '20px', top: '20px' }}
            onClick={onBack}
          />
        </Box>
        {/* Progress bar */}
        <CustomStepper activeStep={currentStep} />

        <Stack
          spacing={4}
          sx={{
            maxWidth: 375,
            width: '100%',
          }}
        >
          <Typography
            fontSize="25px"
            fontWeight="700"
            textAlign="center"
            color={TP_COLORS.gray.darken}
          >
            What&apos;s your goal in this subject?
          </Typography>

          <Stack spacing={2}>
            {goalOptions.map((option) => (
              <Button
                key={option}
                sx={
                  selectedOption === option
                    ? actionButtonSelectStyles
                    : actionButtonStyles
                }
                onClick={() => {
                  setSelectedOption(option);
                  setIsError(false);
                }}
              >
                {option}
              </Button>
            ))}
          </Stack>

          <Stack spacing={1}>
            <Fade in={isError}>
              <FormHelperText error style={{ marginLeft: '2px' }}>
                Please select an option
              </FormHelperText>
            </Fade>
            <DarkLoadingButton
              size="large"
              sx={{ height: '55px', fontSize: '16px !important' }}
              onClick={handleContinue}
              loading={isLoading}
            >
              Let&apos;s Find Tutors
            </DarkLoadingButton>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Step6;
